import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../shared/services/api.service';
import { CrudService } from '../shared/services/crud.service';
import { User, Workspace } from '../_models/user';
import { Role } from '../_models/role';
import { isNil } from 'lodash';
import { Router } from '@angular/router';
import { MpsUserService } from '../../../mps/services/mps-user.service';
import { Permissions } from '../_models/permissions';


@Injectable({
  providedIn: 'root'
})
export class UserService {

  private modelName = 'users';
  currentWorkspace: Workspace | null = null;

  workspaceChanged: EventEmitter<Workspace | null> = new EventEmitter<Workspace | null>();

  constructor(
    private _apiService: ApiService,
    private crudService: CrudService,
    private _userService: MpsUserService,
    private _router: Router
  ) { }

  getUsers(workspaceId?: number): Observable<any> {
    return this._apiService.get(this.modelName + (!isNil(workspaceId) ? ('/' + workspaceId) : '')).pipe(map((res: any) => res.data.map((user: User) => new User(user))));
  }

  getUser(userId: number): Observable<User> {
    return this._apiService.get(this.modelName + '/' + this.getCurrentWorkspace()?.id + '/' + userId).pipe(map((res: any) => new User(res.data)));
  }

  getUserAdmin(userId: number): Observable<User> {
    return this._apiService.get('users-admin/' + userId).pipe(map((res: any) => new User(res.data)));
  }

  create(userData: FormData) {
    return this._apiService.post('users/' + this.getCurrentWorkspace()?.id, userData);
  }

  createAdmin(userData: FormData) {
    return this._apiService.post('users-admin', userData);
  }

  edit(userId: number, userData: FormData) {
    return this._apiService.patch('users/' + this.getCurrentWorkspace()?.id + '/' + userId, userData);
  }

  editAdmin(userId: number, userData: FormData) {
    return this._apiService.patch('users-admin/' + userId, userData);
  }

  delete(userId: number): Observable<any> {
    return this.crudService.deleteResource(this.modelName, userId);
  }

  getCurrentUser() {
    const user = this._userService.getUser();
    return !isNil(user) && !isNil(user.id) ? new User(user) : null;
  }

  public updateUserLangPref(lang: string): Promise<any> {
    return this._apiService.post('user/updateLang', { lang }).toPromise();
  }

  changeCurrentWorkspace(workspace: Workspace): void {
    this.currentWorkspace = workspace;
    localStorage.setItem('currentWorkspace_' + this.getCurrentUser()?.id, JSON.stringify(workspace));
    this.workspaceChanged.emit(workspace);
    this._router.navigate(['/price-list'])
  }

  getCurrentWorkspace(): Workspace | null {
    const workspaceStorageName = 'currentWorkspace_' + this.getCurrentUser()?.id;
    const ws = localStorage.getItem(workspaceStorageName);
    const workspace = !isNil(ws) && ws != 'undefined' ? new Workspace(JSON.parse(ws)) : null;
    return !isNil(workspace) ? workspace : this.currentWorkspace;
  }

  checkResourceActionRight(menu: string, resource: string, action: string): boolean {
    const workspace = this.getCurrentWorkspace();
    return workspace!.role?.permissions[menu as keyof Permissions][resource as keyof Permissions[keyof Permissions]][action] ?? false;
  }

  isWorkspaceAdmin(): boolean {
    return this.getCurrentWorkspace()?.role?.id == 2;
  }

  isWorkspaceMember(): boolean {
    return this.getCurrentWorkspace()?.role?.id == 3;
  }

  isApplicationAdmin(): boolean {
    return this.getCurrentUser()?.roles.find(i => i.id == 1) ? true : false;
  }

  detectWorkspaceChange(): EventEmitter<Workspace> {
    return this.workspaceChanged as EventEmitter<Workspace>;
  }

  initCurrentWorkspace(user: User): void {

    if (!isNil(user)) {
      const appWorkspaces = user.workspaces;

      if (!isNil(this.getCurrentWorkspace())) {
        const workspace = appWorkspaces.find(a => a.id == this.getCurrentWorkspace()!.id);
        this.changeCurrentWorkspace(workspace!);
      }

      if (isNil(this.getCurrentWorkspace()) && !isNil(appWorkspaces) && appWorkspaces.length > 0) {
        this.changeCurrentWorkspace(appWorkspaces[0]);
      }
    }
  }

  getRoles(): Observable<Array<Role>> {
    return this._apiService.get('roles').pipe(map((res: any) => res.data.map((role: Role) => new Role(role))));
  }

  allWithPriceList() {
    return this._apiService.get(this.modelName + '/with-price-list/' + this.getCurrentWorkspace()?.id)
      .pipe(map((res: any) => res.data.map((user: User) => new User(user))));
  }
}
